import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';

const ValueTrackingChart = ({ data, timeframe }) => {
  // Transform data for chart
  const chartData = React.useMemo(() => {
    if (!data || data?.length === 0) return [];
    
    const dateMap = {};
    
    data?.forEach((playerData, index) => {
      playerData?.history?.forEach(point => {
        const dateKey = point?.date;
        if (!dateMap?.[dateKey]) {
          dateMap[dateKey] = { date: dateKey };
        }
        dateMap[dateKey][`player${index}`] = point?.value;
      });
    });
    
    return Object.values(dateMap)?.sort((a, b) => new Date(a?.date) - new Date(b?.date));
  }, [data]);

  const colors = ['var(--color-primary)', 'var(--color-secondary)', 'var(--color-accent)', 'var(--color-success)', 'var(--color-warning)'];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-foreground mb-2">{new Date(label)?.toLocaleDateString()}</p>
          {payload?.map((entry, index) => {
            const playerIndex = parseInt(entry?.dataKey?.replace('player', ''));
            const playerName = data?.[playerIndex]?.player?.fullName || data?.[playerIndex]?.player?.knownAs || 'Unknown';
            return (
              <p key={index} className="text-xs text-muted-foreground">
                <span style={{ color: entry?.color }}>{playerName}: </span>
                <span className="font-semibold">€{(entry?.value / 1000000)?.toFixed(1)}M</span>
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon name="LineChart" size={20} className="text-primary" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-foreground">Value Tracking</h3>
          <p className="text-sm text-muted-foreground">Historical market value movements (Last {timeframe} days)</p>
        </div>
      </div>
      {chartData?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="LineChart" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No historical data available</p>
        </div>
      ) : (
        <div className="w-full h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="date" 
                stroke="var(--color-muted-foreground)" 
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => new Date(value)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              />
              <YAxis 
                stroke="var(--color-muted-foreground)" 
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `€${(value / 1000000)?.toFixed(0)}M`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ fontSize: '12px' }}
                formatter={(value, entry, index) => {
                  const playerIndex = parseInt(value?.replace('player', ''));
                  return data?.[playerIndex]?.player?.knownAs || data?.[playerIndex]?.player?.fullName || `Player ${playerIndex + 1}`;
                }}
              />
              {data?.slice(0, 5)?.map((_, index) => (
                <Line 
                  key={index}
                  type="monotone" 
                  dataKey={`player${index}`}
                  stroke={colors?.[index % colors?.length]}
                  strokeWidth={2}
                  dot={{ fill: colors?.[index % colors?.length], r: 4 }}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default ValueTrackingChart;